import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useEffect, useState } from 'react';
import { Button, Container, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { clearCookies } from 'utils/cookieUtils';
import { IoInformationCircle } from 'react-icons/io5';
import { RxHamburgerMenu } from 'react-icons/rx';
import styles from './PrivacyPopup.module.css';

export const PrivacyPopup = ({
  declineAction = () => {},
  acceptAction = () => {},
  hasAcceptedPolicy = false,
  showFull,
  setShowFull,
  setMobileNavOpen,
}) => {
  const navigate = useNavigate();
  const isMsalAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(showFull);
  const [headerInView, setHeaderInView] = useState(true);

  useEffect(() => {
    if (showFull) {
      setShouldRender(true);
      setIsAnimating(false);
    } else if (shouldRender) {
      setIsAnimating(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsAnimating(false);
      }, 400); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [showFull, shouldRender]);

  useEffect(() => {
    setShowFull(!hasAcceptedPolicy);
  }, [hasAcceptedPolicy, setShowFull]);

  useEffect(() => {
    const header = document.getElementById('mobile-header');
    if (!header) return;
    const observer = new IntersectionObserver(([entry]) => setHeaderInView(entry.isIntersecting), {
      threshold: 0.01,
    });
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  const declineHandler = () => {
    if (isMsalAuthenticated) {
      instance.logoutRedirect({
        onRedirectNavigate: (url) => {
          return false;
        },
      });
    }
    declineAction();
    clearCookies();
    navigate('/');
  };
  const acceptHandler = () => {
    acceptAction();
    setShowFull(false);
  };

  return (
    <>
      {shouldRender ? (
        <div
          className={`position-fixed bottom-0 start-0 w-100 ${
            isAnimating ? styles.slideDown : styles.slideUp
          }`}
          style={{ zIndex: 1100 }}
        >
          <Container fluid className="px-0">
            <Row className="gx-0 align-items-stretch bg-primary-highlight text-white">
              <Col
                xs={12}
                lg={8}
                className="p-3 p-md-4 d-flex flex-column align-items-center text-center gap-3"
              >
                <div className="d-flex align-items-center justify-content-center">
                  <IoInformationCircle size={32} />
                </div>
                <div className="d-flex flex-column gap-2 align-items-center">
                  <h2 className="fw-bold fs-5 mb-0">Privacy &amp; Cookie Policy</h2>
                  <p className="fs-6 mb-0 lh-base">
                    This website uses cookies to ensure you get the best experience on our website.
                    By accessing or using the service, you indicate consent to the following
                    policies:
                  </p>
                  <div className="d-flex flex-column flex-md-row gap-3 justify-content-center">
                    <a
                      href={`/terms`}
                      target="_blank"
                      rel="noreferrer"
                      className="fs-6 text-white text-decoration-underline"
                    >
                      Terms of Use
                    </a>
                    <a
                      href={`/policy`}
                      target="_blank"
                      rel="noreferrer"
                      className="fs-6 text-white text-decoration-underline"
                    >
                      Privacy &amp; Cookie Policy
                    </a>
                  </div>
                </div>
              </Col>
              <Col
                xs={12}
                lg={4}
                className="d-flex flex-column flex-md-row flex-lg-column align-items-center text-center gap-2 p-3 p-md-4 justify-content-center"
              >
                <Button
                  variant="light"
                  className={`w-100 ${styles.acceptButton}`}
                  onClick={acceptHandler}
                >
                  Accept &amp; Allow Cookies
                </Button>
                <Button className={`w-100 ${styles.declineButton}`} onClick={declineHandler}>
                  Decline
                </Button>
              </Col>
            </Row>
          </Container>
        </div>
      ) : (
        <>
          {headerInView ? (
            <Button
              variant="link"
              className="position-fixed bottom-0 start-0 m-3 text-decoration-none text-white rounded-circle p-2 bg-primary-highlight shadow"
              style={{ zIndex: 1100 }}
              onClick={() => setShowFull(true)}
            >
              <IoInformationCircle size={32} />
            </Button>
          ) : (
            <div
              className="position-fixed bottom-0 start-0 m-3 d-flex flex-column align-items-center gap-2 rounded-pill bg-primary-highlight shadow px-2 py-2"
              style={{ zIndex: 1100 }}
            >
              <button
                type="button"
                className="btn btn-link px-1 text-white"
                aria-label="Open menu"
                onClick={() =>
                  setMobileNavOpen?.((prev) => (typeof prev === 'boolean' ? !prev : true))
                }
              >
                <RxHamburgerMenu size={26} />
              </button>
              <button
                type="button"
                className="btn btn-link p-0 m-0 text-white"
                aria-label="Open terms and privacy info"
                onClick={() => setShowFull(true)}
              >
                <IoInformationCircle size={28} />
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
};
